import { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { formatLocation } from '@rocco/utils/format-location';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import type { SupportedCountry } from 'generated/graphql';
import { useCountryContext } from '@archipro-website/localisation';
import { usePlaceFinderDirect } from '~/ui-rocco/hooks/location/use-place-finder-direct';
import {
    GateFieldLabel,
    GateInput,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';

/**
 * The parts of a place HubSpot has options for.
 *
 * Country and region only: the portal's form has no city field, and both of these are
 * dropdowns, so the server maps them onto the exact option values before sending.
 */
export interface SubscribeLocation {
    Region?: string;
    Country?: string;
}

interface SubscribeLocationFieldProps {
    /** What the reader has typed or picked, shown in the input. */
    value: string;
    onChange: (label: string, place: SubscribeLocation | null) => void;
    id?: string;
}

/**
 * Suburb search for the subscribe modal, resolving to the country and region
 * HubSpot holds options for.
 *
 * A typed string would be a value nobody can segment on — "Ponsonby", "auckland",
 * "AKL" are the same place and none of them match a filter. Picking a suggestion
 * resolves the parts, which the server then maps onto the portal's own dropdown
 * options.
 *
 * Typing without picking clears the resolved parts on purpose: half a match is
 * worse than none, because it looks segmented and is not.
 */
export const SubscribeLocationField = ({
    value,
    onChange,
    id,
}: SubscribeLocationFieldProps) => {
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const boxRef = useRef<HTMLDivElement>(null);

    // Search the market the reader is browsing, so a NZ visitor is not offered
    // Australian suburbs.
    const { code: countryCode } = useCountryContext();
    const { places, loading, handlePlaceFind, resetPlaces } =
        usePlaceFinderDirect({ country: countryCode as SupportedCountry });

    // A click anywhere else is a dismissal — without this the suggestions sit
    // over the Subscribe button and swallow the first tap on it.
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: PointerEvent) => {
            if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    const select = (place: DetailedPlace) => {
        onChange(formatLocation(place), {
            // `region` carries the state everywhere except Australia, which puts it in
            // `state` and leaves `region` empty. The server matches whichever arrives
            // against HubSpot's list for the country, so an unrecognised one is dropped
            // rather than sent.
            Region: place.region || place.state,
            Country: place.country,
        });
        setOpen(false);
        setHighlighted(-1);
        resetPlaces();
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (!open || places.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((i) => (i + 1) % places.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((i) => (i <= 0 ? places.length - 1 : i - 1));
        } else if (event.key === 'Enter' && highlighted >= 0) {
            // Only when a suggestion is highlighted — otherwise Enter belongs to
            // the form, and stealing it means the reader cannot submit from here.
            event.preventDefault();
            const place = places[highlighted];
            if (place) select(place);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <GateFieldLabel htmlFor={id}>Location</GateFieldLabel>
            <div className="relative" ref={boxRef}>
                <GateInput
                    id={id}
                    type="text"
                    // This field runs its own suggestions, so the browser's
                    // autofill list must not cover them.
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    placeholder="Suburb or city"
                    className="pr-9"
                    value={value}
                    onKeyDown={onKeyDown}
                    onChange={(event) => {
                        // Typing invalidates whatever was picked — see the note
                        // above about half a match.
                        onChange(event.target.value, null);
                        setHighlighted(-1);
                        setOpen(true);
                        void handlePlaceFind(event.target.value, 6);
                    }}
                    onFocus={() => {
                        if (value) setOpen(true);
                    }}
                />
                {loading ? (
                    <Loader2
                        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-black/30"
                        aria-hidden="true"
                    />
                ) : (
                    <Search
                        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-black/50"
                        aria-hidden="true"
                    />
                )}
                {open && places.length > 0 ? (
                    <ul
                        role="listbox"
                        className="absolute top-[calc(100%+0.25rem)] right-0 left-0 z-20 m-0 max-h-56 list-none overflow-y-auto rounded-[0.625rem] bg-white py-1 shadow-[0_0.25rem_1rem_rgba(0,0,0,0.15)]"
                    >
                        {places.map((place: DetailedPlace, index: number) => {
                            const label = formatLocation(place);
                            return (
                                <li key={place.addressID ?? label}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={index === highlighted}
                                        onMouseEnter={() =>
                                            setHighlighted(index)
                                        }
                                        onClick={() => select(place)}
                                        className={cn(
                                            'block w-full px-2.5 py-2 text-left text-[max(1rem,16px)] tracking-[-0.02em] text-[#161616] md:text-[1rem]',
                                            index === highlighted &&
                                                'bg-[#f6f6f6]',
                                        )}
                                    >
                                        {label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>
        </div>
    );
};
